import angular from 'angular';
import { XMLNode } from 'app/libs/XmlNode';

import {
    FiscalUtilsService
} from 'app/modules/core/service/fiscal-utils/fiscal-utils';

import _ from 'lodash';

import {
    ConfigurationManagerService,
    ErrorsLoggerService
} from 'src/app/core';

import {
    FiscalPayment
} from 'src/app/shared/model/it-fiscal-printer.model';

import {
    groupBy,
    keyBy,
    matches,
    MathUtils
} from 'src/app/shared/utils';

import {
    Departments,
    Printers,
    Sales,
    SalesItems,
    SalesItemsPriceChanges,
    SalesPriceChanges
} from 'tilby-models';

const idleStates: Record<string, string> = {
    0: 'INACTIVE', // ok
    1: 'PRINT_IN_PROGRESS', // ~ok
    2: 'PAYMENT_IN_PROGRESS', // ~ok
    3: 'OPEN_DOCUMENT', // ~ok
    4: 'ACTIVE_FIDELITY' // ok
};

type RCHPaymentConfig = {
    id: number,
    name: string,
    enableChange: number,
    enableSum: number,
    unclaimed: number,
    enableTicket?: number
}

// Helper to safely obtain textContent
function getTextContent(element: Element | null | undefined, defaultValue: string = ''): string {
    return element?.textContent || defaultValue;
}

// Helper to safely obtain a number from textContent or from an attribute
function getNumberContent(element: Element | null | undefined, attributeName?: string, defaultValue: number = 0): number {
    const value = attributeName ? element?.getAttribute(attributeName) : element?.textContent;
    return Number(value) || defaultValue;
}

type RCHFiscalBodyOptions = {
    printNotes?: boolean;
    xml7Mode?: boolean;
    isRefundingRT?: boolean;
};

export class RCHUtils {
    private paymentsChecked: Record<string, boolean> = {};

    static $inject = [
        "fiscalUtils",
        "errorsLogger",
        "checkManager"
    ];

    constructor(
        private fiscalUtilsService: FiscalUtilsService,
        private errorsLoggerService: ErrorsLoggerService,
        private configurationManagerService: ConfigurationManagerService
    ) {
    }

    private removeTicketFromCheck = function (row: RCHPaymentConfig) {
        const { enableTicket, ...payment } = row;

        return payment;
    }

    public cleanUpSpecialChars(str?: string) {
        return _.deburr(str)
            .replace('\u001B', '')
            .replace(/\'/, "")
            .replace(/\"/, "")
            .replace("€", "EUR0")
            .replace("TOTALE", "T0TALE")
            .replace(String.fromCharCode(160), ' ');
    }

    public addTextLine(text?: string) {
        return ['="', `(${this.cleanUpSpecialChars(text)})`];
    }

    public addBorder(char: string, columns: number = 48) {
        return ['="', `(${this.cleanUpSpecialChars(char).repeat(columns)})`];
    }

    public linesToCommands(lines: string | string[]) {
        const linesCommands = [];

        if (typeof lines === 'string') {
            lines = lines.split("\n");
        }

        if (Array.isArray(lines)) {
            for (const line of lines) {
                linesCommands.push([`="`, `(${this.cleanUpSpecialChars(line)})`]);
            }
        }

        return [['=K'], ['=o'], ...linesCommands, ['=o']];
    }
    /**
     *  checkStatus
     *  @param printer
     *  @return INACTIVE | PRINT_IN_PROGRESS | PAYMENT_IN_PROGRESS | OPEN_DOCUMENT | ACTIVE_FIDELITY
     */
    private async checkStatus(printer: Printers) {
        const response = await this.sendCommands(printer, this.cmdToXml([['<<', '?s']]), { timeout: 3000 });

        const xmlParser = new DOMParser();
        const xmlDoc = xmlParser.parseFromString(response, 'application/xml');
        const idleIndex = getTextContent(xmlDoc.querySelector("idleState"));

        return [idleStates[idleIndex], response]
    }

    public async isAvailable(printer: Printers) {
        const [_, response] = await this.checkStatus(printer);

        return response;
    }

    public async fixIssues(printer: Printers, error: unknown) {
        if (typeof error !== 'string' || !error.startsWith('RCH.ERROR_')) {
            throw error;
        }

        // Check stampante
        const [status] = await this.checkStatus(printer);

        this.errorsLoggerService.debug(`[ RCHUtils ] Status:`, status);

        let commandsToSend: string[][] = [];
        let finalStatus: 'OK' | 'CANCELED' | undefined;

        switch (status) {
            case 'CONNECTION_ERROR':
            case 'BUSY':
                finalStatus = status;
                break;
            case 'INACTIVE':
            case 'PRINT_IN_PROGRESS':
                // ANNULLA SCONTRINO
                finalStatus = 'CANCELED';
                commandsToSend = [['=k']];
                break;
            case 'PAYMENT_IN_PROGRESS':
                finalStatus = "OK";
                // Forza completamento pagamento e invia nuovo scontrino
                commandsToSend = [
                    ['=T'],
                    ['="', '()'],
                    ['="', '(###############################################)'],
                    ['="', '(####                                      #####)'],
                    ['="', '(####   ATTENZIONE: VERIFICARE PAGAMENTO   #####)'],
                    ['="', '(####                                      #####)'],
                    ['="', '(###############################################)'],
                    ['="', '()'],
                    ['=c']
                ];
                break;
            case 'OPEN_DOCUMENT':
            case 'ACTIVE_FIDELITY':
                finalStatus = 'OK';
                commandsToSend = [['=c']];
                break;
            default: break;
        }

        if (commandsToSend.length) {
            await this.sendCommands(printer, this.cmdToXml(commandsToSend));
        }

        if (finalStatus !== 'OK') {
            throw finalStatus;
        }
    }

    public cmdToXml(commands: string[][]) {
        const header = `<?xml version="1.0" encoding="UTF-8"?>`;
        const root = new XMLNode();
        const cmdXml = new XMLNode("Service");

        root.appendChild(cmdXml);

        for (const command of commands) {
            const cmd = Array.isArray(command) ? command.join('/') : command;
            cmdXml.appendChild(new XMLNode('cmd', null, cmd));
        }

        return header + root.toString();
    }

    public displaySaleTotal(printer: Printers, sale: Sales) {
        const displayXml = this.cmdToXml([
            ['=D1', '(T0TALE)'],
            ['=D2', `(EURO ${sale.final_amount!.toFixed(2).replace(".", ",")})`]
        ]);

        return this.sendCommands(printer, displayXml);
    }

    public getPaymentsConfig(options?: { xml7Mode?: boolean }): RCHPaymentConfig[] {
        return [
            { id: 1, name: "Contanti", enableChange: 1, enableSum: 1, unclaimed: 0, enableTicket: 0 },
            { id: 2, name: "Assegni", enableChange: 0, enableSum: 0, unclaimed: 0, enableTicket: 0 },
            { id: 3, name: "Pagam. elettronico", enableChange: 0, enableSum: 0, unclaimed: 0, enableTicket: 0 },
            { id: 4, name: "Ticket", enableChange: 0, enableSum: 0, unclaimed: options?.xml7Mode ? 0 : 1, enableTicket: 1 },
            { id: 5, name: "Non riscosso Beni", enableChange: 0, enableSum: 0, unclaimed: options?.xml7Mode ? 1 : 1, enableTicket: 0 },
            { id: 6, name: "Non riscosso Servizi", enableChange: 0, enableSum: 0, unclaimed: options?.xml7Mode ? 2 : 1, enableTicket: 0 },
            { id: 7, name: "Fattura differita", enableChange: 0, enableSum: 0, unclaimed: options?.xml7Mode ? 3 : 1, enableTicket: 0 },
            { id: 8, name: "Non riscosso SSN", enableChange: 0, enableSum: 0, unclaimed: options?.xml7Mode ? 4 : 1, enableTicket: 0 },
            { id: 9, name: "Buono multiuso", enableChange: 0, enableSum: 0, unclaimed: options?.xml7Mode ? 5 : 1, enableTicket: 0 },
            { id: 10, name: "Sconto a pagare", enableChange: 0, enableSum: 0, unclaimed: options?.xml7Mode ? 5 : 1, enableTicket: 0 }
        ];
    }
    /**
     *  sendCommands
     *  @param printer
     *  @param command
     *  @return INACTIVE | PRINT_IN_PROGRESS | PAYMENT_IN_PROGRESS | OPEN_DOCUMENT | ACTIVE_FIDELITY
    */
    public async sendCommands(printer: Printers, command: string, options?: { timeout?: number }) {
        const strURL = `http://${printer.ip_address}/service.cgi`;
        const reqHeaders = { 'Content-Type': 'application/xml' };

        const responseObj = await this.fiscalUtilsService.sendRequest(strURL, {
            data: command,
            method: 'POST',
            timeout: options?.timeout,
            headers: reqHeaders
        }).catch(err => { throw 'CONNECTION_ERROR'; });

        const response = responseObj.data;
        const xmlParser = new DOMParser();
        const xmlDoc = xmlParser.parseFromString(response, 'application/xml');

        if (getTextContent(xmlDoc.querySelector("paperEnd")) !== '0') {
            throw 'PAPER_END';
        } else if (getTextContent(xmlDoc.querySelector("coverOpen")) !== '0') {
            throw 'COVER_OPEN';
        } else if (getTextContent(xmlDoc.querySelector("busy")) !== '0') {
            throw 'BUSY';
        } else {
            const errorCode = getTextContent(xmlDoc.querySelector("errorCode"));
            const printerError = getTextContent(xmlDoc.querySelector("printerError"));

            if (errorCode !== '0') {
                switch (errorCode) {
                    case '93':
                        throw 'FISCAL_CLOSING_NEEDED';
                    default:
                        throw `RCH.ERROR_${errorCode}`;
                }
            } else if (printerError !== '0') {
                throw `RCH.ERROR_${printerError}`;
            }
        }

        return response;
    }

    public async checkPaymentsTable(printer: Printers, printerInfo: any, options?: { xml7Mode?: boolean }) {
        const printerSerial = printerInfo.printer_serial;

        if (this.paymentsChecked[printerSerial] || this.configurationManagerService.getSetting('fiscalprinters.override_payments_check')) {
            return;
        }

        const response = await this.sendCommands(printer, this.cmdToXml([['<<', '?C']]));
        let referenceTable = this.getPaymentsConfig(options);

        let currentTableOk = true;

        try {
            const xmlParser = new DOMParser();
            const xmlDoc = xmlParser.parseFromString(response, 'application/xml');
            const payments = xmlDoc.querySelectorAll('Payment');

            let currentTable: RCHPaymentConfig[] = Array.from(payments).map((paymentElement) => {
                const creditTypeSelector = options?.xml7Mode
                    ? 'CreditType value'
                    : 'credit enabled';

                return {
                    id: getNumberContent(paymentElement, 'id'),
                    name: getTextContent(paymentElement.querySelector('txt')),
                    enableChange: getNumberContent(paymentElement.querySelector('change enabled')),
                    enableSum: getNumberContent(paymentElement.querySelector('cash enabled')),
                    enableTicket: getNumberContent(paymentElement.querySelector('Ticket enabled')),
                    unclaimed: getNumberContent(paymentElement.querySelector(creditTypeSelector))
                };
            });

            if (!options?.xml7Mode) {
                currentTable = currentTable.map((payment) => this.removeTicketFromCheck(payment));
                referenceTable = referenceTable.map((payment) => this.removeTicketFromCheck(payment));
            }

            const currentPayments = keyBy(currentTable, p => p.id);
            currentTableOk = referenceTable.every((payment) => matches(payment, currentPayments[payment.id]));
        } catch (err) {
            this.errorsLoggerService.warn('[ RCHUtils ] Error checking payments table:', err);
            return;
        }

        if (!currentTableOk) {
            throw 'CONFIGURATION_NEEDED';
        }

        this.paymentsChecked[printerSerial] = true;
    }

    public extractPayments(sale: Sales, resources?: { departments?: Departments[] }, options?: { xml7Mode?: boolean }) {
        const xml7Mode = options?.xml7Mode;
        const payments = this.fiscalUtilsService.extractPayments(sale);

        let servicesToPay = 0;
        let goodsToPay = 0;

        if (xml7Mode && Array.isArray(resources?.departments)) {
            const departmentsByCode = keyBy(resources.departments, (department) => department.printer_code);
            const itemsBySalesType = groupBy(sale.sale_items || [], (saleItem) => departmentsByCode[saleItem.department_id]?.sales_type);

            goodsToPay = MathUtils.round((itemsBySalesType.goods || []).reduce((amount, saleItem) => amount + saleItem.final_price * saleItem.quantity, 0) || 0);
            servicesToPay = MathUtils.round((itemsBySalesType.services || []).reduce((amount, saleItem) => amount + saleItem.final_price * saleItem.quantity, 0) || 0);
        }

        const getUnclaimedAmount = (payment: FiscalPayment): (FiscalPayment | FiscalPayment[]) => {
            if (!xml7Mode) {
                return {
                    ...payment,
                    payment_type: 6
                }
            }

            const servicesAmount = Math.min(servicesToPay, payment.amount);
            const goodsAmount = Math.max(0, MathUtils.round(payment.amount - servicesAmount));

            servicesToPay = MathUtils.round(servicesToPay - servicesAmount);
            goodsToPay = MathUtils.round(goodsToPay - goodsAmount);

            return [
                {
                    ...payment,
                    amount: MathUtils.round(servicesAmount),
                    payment_type: 6
                },
                {
                    ...payment,
                    amount: MathUtils.round(goodsAmount),
                    payment_type: 5
                }
            ].filter(p => !!p.amount);
        }

        const rchPayments = payments.flatMap((payment) => {
            //Assign payment type
            switch (payment.method_type_id) {
                case 1: case 19: case 21: case 32: case 38: case 39: case 40:
                    payment.payment_type = 1; //Cash
                    break;
                case 3:
                    payment.payment_type = 2; //Cheque
                    break;
                case 4: case 5: case 8: case 11: case 13: case 14: case 15: case 17: case 18: case 27: case 30: case 31: case 35: case 37: case 44: case 45:
                    payment.payment_type = 3; //Credit / Credit card
                    break;
                case 6: case 34:
                    payment.payment_type = 4; //Ticket
                    break;
                case 2: //Unclaimed
                case 20:
                    return getUnclaimedAmount(payment);
                case 10: case 33:
                    payment.payment_type = 9;
                    break;
                case 22: case 23: case 24: case 28: case 29: case 36: case 41: case 43:
                    payment.payment_type = 7;
                    break;
                case 25:
                    payment.payment_type = 8;
                    break;
                case 26:
                    if (payment.payment_data?.rounding) {
                        return null;
                    }

                    payment.payment_type = 10;
                    break;
                case 16: case 42: case 46:
                    //(Ticket if unclaimed, digital if otherwise)
                    if (payment.unclaimed) {
                        return getUnclaimedAmount(payment);
                    }

                    payment.payment_type = 3;
                    break;
                default:
                    payment.payment_type = 1;
                    break;
            }

            return payment;
        }).filter(p => !!p) as FiscalPayment[];

        const electronicPayments = rchPayments.filter(p => p.payment_type === 3);

        if (!electronicPayments.length || !this.configurationManagerService.getSetting('fiscalprinters.rch.electronic_payments_last')) {
            return rchPayments;
        }

        const otherPayments = rchPayments.filter(p => p.payment_type !== 3);

        const mergedElectronicPayment: FiscalPayment = {
            ...electronicPayments[0],
            amount: MathUtils.round(electronicPayments.reduce((sum, payment) => sum + payment.amount, 0)),
            method_name: "Pagam. elettronico"
        };

        return [
            ...otherPayments,
            mergedElectronicPayment
        ];
    }

    private getPriceChangeCommand(priceChange: SalesPriceChanges | SalesItemsPriceChanges, amount: number, options?: { xml7Mode?: boolean }) {
        const pcAmount = Math.abs(Math.round(amount * 100));
        let command;

        switch (priceChange.type) {
            case 'discount_fix':
            case 'discount_perc':
                command = ['=V-', '$' + pcAmount];
                break;
            case 'surcharge_fix':
            case 'surcharge_perc':
                command = ['=V+', '$' + pcAmount];
                break;
            case 'gift':
                if (!options?.xml7Mode) {
                    command = ['=V-', '$' + pcAmount];
                }
                break;
            default:
                return;
        }

        if (command && priceChange.description) {
            command.push(`(${priceChange.description})`);
        }

        return command;
    }

    public applyPriceChanges(priceChanges: SalesPriceChanges[] | SalesItemsPriceChanges[], options?: { xml7Mode?: boolean }) {
        const commands: string[][] = [];

        for (const pc of priceChanges) {
            if(pc.amount) {
                const pcCommand = this.getPriceChangeCommand(pc, pc.amount, options);

                if (pcCommand) {
                    commands.push(pcCommand);
                }
            }
        }

        return commands;
    }

    /**
     * Generates commands for items, discounts, surcharges and subtotals sequence.
     */
    public applyFiscalBody(sale: Sales, options: RCHFiscalBodyOptions) {
        const { isRefundingRT, printNotes, xml7Mode } = options;
        const commands: (string | string[])[] = [];

        const appendItems = (items: SalesItems[]) => {
            for (const si of items) {
                commands.push(...this.saleItemToCommands(si, { printNotes, xml7Mode, isRefundingRT }));
            }
        }

        const applyPriceChanges = (priceChanges: SalesPriceChanges[] | SalesItemsPriceChanges[]) => {
            commands.push(...this.applyPriceChanges(priceChanges, { xml7Mode }))
        }

        const applySubtotal = () => {
            commands.push(['=S']);
        }

        this.fiscalUtilsService.applyFiscalBody(sale, appendItems, applyPriceChanges, applySubtotal);

        return commands;
    }

    public saleItemToCommands(saleItem: SalesItems, options?: { isRefundingRT?: boolean, printNotes?: boolean, xml7Mode?: boolean }) {
        const commands = [];
        var cmd = ['=R' + saleItem.department_id, '$' + Math.round(saleItem.price * 100), '*' + Math.abs(saleItem.quantity)];

        if (options?.isRefundingRT) {
            commands.push(cmd);
        } else {
            if (options?.xml7Mode) {
                switch (saleItem.type) {
                    case 'deposit_cancellation':
                        cmd.push('&2');
                        break;
                    case 'coupon':
                        cmd.push('&3');
                        break;
                    case 'sale':
                    default:
                        if (saleItem.price_changes?.some(pc => pc.type === 'gift')) {
                            cmd.push('&1');
                        }
                        break;
                }
            } else if (saleItem.type === 'refund') {
                commands.push('=r');
            }

            // name (or department_name), quantity, price
            cmd.push(`(${this.cleanUpSpecialChars(saleItem.name || saleItem.department_name)})`);

            commands.push(cmd);

            // Check printNotes
            const printNotes = options?.printNotes !== false;

            // Item barcode (if print_notes)
            if (printNotes && saleItem.barcode) {
                if (saleItem.barcode.toLowerCase().indexOf('p') < 0 && saleItem.barcode.toLowerCase().indexOf('q') < 0) {
                    commands.push(['="', `(# ${(saleItem.barcode || '').trim()} )`]);
                }
            }

            // Notes
            if (printNotes && saleItem.notes) {
                const notesLines = saleItem.notes.split("\n");
                for (const nl of notesLines) {
                    if (nl.trim()) {
                        commands.push(['="', `(# ${this.cleanUpSpecialChars(nl.trim())} )`]);
                    }
                }
            }

            if (saleItem.quantity < 0 && saleItem.refund_cause_description) { // Refund cause
                commands.push(['="', `(# ${this.cleanUpSpecialChars(saleItem.refund_cause_description)} )`]);
            } else if (saleItem.quantity > 0) { //Discount / Surcharges
                const priceChangeCommands = this.applyPriceChanges(saleItem.price_changes || [], options);

                Array.prototype.push.apply(commands, priceChangeCommands);
            }
        }

        return commands;
    }

    public saleToCourtesyReceiptCommands(sale: Sales) {
        const commands = [];

        // Send CL
        commands.push(['=K']);
        commands.push(['=o']);

        commands.push(this.addTextLine('SCONTRINO DI CORTESIA'));
        commands.push(this.addTextLine());

        // Riga non fiscale con l'asid
        commands.push(this.addTextLine(sale.name));
        commands.push(this.addTextLine());

        // Items
        for (const si of sale.sale_items || []) {
            commands.push(this.addTextLine(`${si.quantity}x ${this.cleanUpSpecialChars(si.name || si.department_name)}`.padEnd(48, ' ')));
        }

        if (sale.id) {
            commands.push(['="', '$4', `(${sale.id.toString()})`]);
        }

        commands.push(["=o"]);

        return commands;
    }
}

angular.module('printers').service('rchUtils', RCHUtils)